import type { WorkspacePersonaRole } from '@myplayer/rbac';
import { IconExternalLink, IconPlus } from '@myplayer/ui';
import { doc } from 'firebase/firestore';
import { useEffect, useRef, type ReactElement } from 'react';
import { Badge, Button, EmptyState } from './components/ui/primitives.js';
import { getFirebaseClients } from './firebase.js';
import {
  normalizeGoogleAnalyticsState,
  type ConnectGoogleAnalyticsStreamPayload,
  type ConnectGoogleAnalyticsStreamResult,
  type DisconnectGoogleAnalyticsIntegrationPayload,
  type DisconnectGoogleAnalyticsIntegrationResult,
  type GoogleAnalyticsAccountOption,
  type GoogleAnalyticsIntegrationState,
  type SaveGoogleAnalyticsIntegrationPayload,
  type SaveGoogleAnalyticsIntegrationResult,
  type SendGoogleAnalyticsTestEventPayload,
  type SendGoogleAnalyticsTestEventResult,
} from './integrations-helpers.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';

export type IntegrationCatalogId = 'google_analytics';

export interface IntegrationsSectionProps {
  readonly initialGoogleAnalytics?: Partial<GoogleAnalyticsIntegrationState>;
  readonly initialAddedIntegrations?: readonly IntegrationCatalogId[];
  readonly googleAnalyticsAccounts?: readonly GoogleAnalyticsAccountOption[];
  readonly onConnectGoogleAnalyticsOAuth?: () => Promise<readonly GoogleAnalyticsAccountOption[]>;
  readonly onConnectGoogleAnalyticsStream?: (
    payload: ConnectGoogleAnalyticsStreamPayload,
  ) => Promise<ConnectGoogleAnalyticsStreamResult>;
  readonly onDisconnectGoogleAnalytics?: (
    payload: DisconnectGoogleAnalyticsIntegrationPayload,
  ) => Promise<DisconnectGoogleAnalyticsIntegrationResult>;
  readonly onSaveGoogleAnalytics?: (
    payload: SaveGoogleAnalyticsIntegrationPayload,
  ) => Promise<SaveGoogleAnalyticsIntegrationResult>;
  readonly onSendGoogleAnalyticsTestEvent?: (
    payload: SendGoogleAnalyticsTestEventPayload,
  ) => Promise<SendGoogleAnalyticsTestEventResult>;
}

export function useWorkspaceGoogleAnalyticsSubscription(params: {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly skip: boolean;
  readonly onUpdate: (norm: GoogleAnalyticsIntegrationState) => void;
}): void {
  const { orgId, workspaceId, skip, onUpdate } = params;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  useEffect(() => {
    if (!orgId || !workspaceId || skip) return;
    let unsubscribe: (() => void) | undefined;
    let active = true;
    void (async () => {
      try {
        const { onSnapshot } = await import('firebase/firestore');
        if (!active) return;
        const wsRef = doc(
          getFirebaseClients().db,
          'organisations',
          orgId,
          'workspaces',
          workspaceId,
        );
        unsubscribe = onSnapshot(wsRef, (snap) => {
          const raw = (
            snap.data() as
              | { readonly googleAnalyticsIntegration?: Partial<GoogleAnalyticsIntegrationState> }
              | undefined
          )?.googleAnalyticsIntegration;
          if (!snap.exists() || !raw) return;
          onUpdateRef.current(normalizeGoogleAnalyticsState(raw));
        });
      } catch {
        // Ignore in non-Firestore test environments
      }
    })();
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [orgId, workspaceId, skip]);
}

export const AVAILABLE_INTEGRATIONS = [
  {
    id: 'google_analytics' as const,
    name: 'Google Analytics 4',
    summary: 'Sign in with Google to connect a GA4 property and forward QR scan events',
  },
] as const;

export function IntegrationsHeaderBar(p: {
  readonly workspaceId: string;
  readonly canManage: boolean;
  readonly personaRole: WorkspacePersonaRole | undefined;
  readonly busy: boolean;
  readonly hasGaTile: boolean;
  readonly onAddGaTile: () => void;
  readonly menuOpen: boolean;
  readonly setMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
}): ReactElement {
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!p.menuOpen || typeof document === 'undefined') return;
    const onPointerDown = (e: MouseEvent): void => {
      if (menuRef.current && e.target instanceof Node && !menuRef.current.contains(e.target)) {
        p.setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') p.setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [p]);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-[15px] font-semibold text-fg">Workspace Integrations</h2>
        <p className="text-[12px] text-fg-secondary">
          Connect analytics and external services for{' '}
          <span className="font-mono text-fg">{p.workspaceId}</span>
        </p>
      </div>
      {p.canManage ? (
        <div ref={menuRef} className="relative inline-flex">
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={p.busy}
            aria-haspopup="menu"
            aria-expanded={p.menuOpen}
            data-testid="add-integration-btn"
            onClick={() => p.setMenuOpen((prev) => !prev)}
          >
            <IconPlus />
            <span>Add integration</span>
          </Button>
          {p.menuOpen && (
            <div
              role="menu"
              aria-label="Available integrations"
              data-testid="add-integration-menu"
              className="absolute right-0 top-full z-50 mt-2 min-w-[320px] rounded-card border border-border bg-surface p-2 shadow-modal"
            >
              <div className="px-3 py-1.5 text-[11px] font-semibold text-fg-muted">
                Available integrations
              </div>
              {AVAILABLE_INTEGRATIONS.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  variant="ghost"
                  size="inline"
                  data-testid={`add-integration-option-${item.id}`}
                  onClick={p.onAddGaTile}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-control px-3 py-2.5 text-left transition-colors hover:bg-surface-subtle"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="text-[13px] font-semibold leading-snug text-fg">
                      {item.name}
                    </div>
                    <div className="text-[12px] font-normal leading-relaxed text-fg-secondary">
                      {item.summary}
                    </div>
                  </div>
                  <Badge variant={p.hasGaTile ? 'outline' : 'accent'}>
                    {p.hasGaTile ? 'Added' : 'Add'}
                  </Badge>
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <span
          role="status"
          data-testid="integrations-readonly-notice"
          className="text-[11px] font-medium text-fg-muted"
        >
          {formatCapabilityRestrictionNotice({
            capability: 'domain:manage',
            actionSummary: 'configure Google Analytics and workspace integrations',
            callerRole: p.personaRole,
          })}
        </span>
      )}
    </div>
  );
}

export function IntegrationsEmptyCatalogState(): ReactElement {
  return (
    <EmptyState
      data-testid="integrations-empty-state"
      icon={<IconExternalLink />}
      title="No integrations added yet"
      description="Click Add integration to pick from available connectors such as Google Analytics 4."
    />
  );
}
